/** Browser-owned upload metadata. No module state; shared by the manager and plain forms. */
export interface VideoMetadata {
    duration_s: number;
    width: number;
    height: number;
    mime: string;
}

export const VIDEO_METADATA_UNAVAILABLE = "video-metadata-unavailable: Cannot read this video's metadata; choose a playable MP4.";

export interface VideoMetadataEnvironment {
    createVideo(): HTMLVideoElement;
    createObjectURL(file: File): string;
    revokeObjectURL(url: string): void;
    timeoutMs: number;
}

const browserEnvironment = (): VideoMetadataEnvironment => ({
    createVideo: () => document.createElement('video'),
    createObjectURL: (file) => URL.createObjectURL(file),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    timeoutMs: 15000,
});

export function readVideoMetadata(
    file: File,
    signal?: AbortSignal,
    environment: VideoMetadataEnvironment = browserEnvironment(),
): Promise<VideoMetadata> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) { reject(new Error('upload aborted')); return; }
        if (!/\.mp4$/i.test(file.name)) { reject(new Error(VIDEO_METADATA_UNAVAILABLE)); return; }
        let video: HTMLVideoElement | undefined;
        let url: string | undefined;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let finished = false;
        const finish = (error?: string): void => {
            if (finished) return;
            finished = true;
            const metadata = video && { duration_s: video.duration, width: video.videoWidth, height: video.videoHeight, mime: 'video/mp4' };
            clearTimeout(timer);
            signal?.removeEventListener('abort', abort);
            if (video) {
                video.onloadedmetadata = null;
                video.onerror = null;
                video.removeAttribute('src');
                video.load();
            }
            if (url !== undefined) environment.revokeObjectURL(url);
            if (error || !metadata || ![metadata.duration_s, metadata.width, metadata.height].every((n) => Number.isFinite(n) && n > 0)) {
                reject(new Error(error || VIDEO_METADATA_UNAVAILABLE));
            } else resolve(metadata);
        };
        const abort = (): void => finish('upload aborted');
        try {
            video = environment.createVideo();
            video.preload = 'metadata';
            video.onloadedmetadata = () => finish();
            video.onerror = () => finish(VIDEO_METADATA_UNAVAILABLE);
            signal?.addEventListener('abort', abort, { once: true });
            timer = setTimeout(() => finish(VIDEO_METADATA_UNAVAILABLE), environment.timeoutMs);
            url = environment.createObjectURL(file);
            video.src = url;
            video.load();
        } catch { finish(VIDEO_METADATA_UNAVAILABLE); }
    });
}

export function videoMetadataFields(metadata: readonly VideoMetadata[]): Array<[string, string]> {
    return metadata.flatMap((entry) => [
        ['video_duration_s[]', String(entry.duration_s)],
        ['video_width[]', String(entry.width)],
        ['video_height[]', String(entry.height)],
        ['video_mime[]', entry.mime],
    ] as Array<[string, string]>);
}

/** Decode the entire batch before exposing any upload payload. */
export async function prepareVideoMetadata(
    files: readonly File[], signal?: AbortSignal, environment?: VideoMetadataEnvironment,
): Promise<Array<[string, string]>> {
    const metadata: VideoMetadata[] = [];
    for (const file of files) metadata.push(await readVideoMetadata(file, signal, environment));
    return videoMetadataFields(metadata);
}

export function mountVideoMetadataForms(): void {
    document.querySelectorAll<HTMLFormElement>('form[data-ft-video-metadata]').forEach((form) => {
        if (form.dataset.ftVideoMetadataMounted) return;
        form.dataset.ftVideoMetadataMounted = '1';
        let pending = false;
        const message = document.createElement('p');
        message.setAttribute('role', 'status');
        form.append(message);
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            if (pending) return;
            const input = form.querySelector<HTMLInputElement>('input[type="file"][name="video_file[]"]');
            const files = Array.from(input?.files || []);
            if (!files.length) { message.textContent = 'Choose an MP4 to upload.'; return; }
            pending = true;
            message.textContent = 'Reading video metadata…';
            void prepareVideoMetadata(files).then((fields) => {
                const current = Array.from(input?.files || []);
                if (files.length !== current.length || files.some((file, i) => file !== current[i])) throw new Error('Video selection changed; submit again.');
                form.querySelectorAll('[data-ft-video-metadata-value]').forEach((node) => node.remove());
                for (const [name, value] of fields) {
                    const hidden = document.createElement('input');
                    hidden.type = 'hidden';
                    hidden.name = name;
                    hidden.value = value;
                    hidden.dataset.ftVideoMetadataValue = '1';
                    form.append(hidden);
                }
                // Native submission bypasses this handler without a recursive requestSubmit().
                HTMLFormElement.prototype.submit.call(form);
            }).catch((error: unknown) => {
                message.textContent = error instanceof Error ? error.message : VIDEO_METADATA_UNAVAILABLE;
            }).finally(() => { pending = false; });
        });
    });
}
